/*
  Product page helpers (loaded only on product templates).

  Accelerated checkout (Shop Pay, Apple Pay …) skips the cart, so on its own it does not carry the
  Globo "personalization" text into the order. The button stays visible at all times; while a
  shopper has entered a personalization, a click on it is caught before Shopify handles it. The
  product is added through the regular Add to cart flow (so Globo's properties travel with it)
  and the shopper continues straight to Shop Pay.
*/
(() => {
  const BLOCK = '.accelerated-checkout-block';
  const SHOP_PAY_URL = '/checkout?payment=shop_pay';
  const CHECKOUT_URL = '/checkout';

  const isFilled = (field) => {
    if (field.type === 'hidden' || field.disabled) return false;
    if (field.type === 'checkbox' || field.type === 'radio') return field.checked;
    return field.value.trim() !== '';
  };

  // Globo draws its fields beside the product form, not inside it, so match them by section.
  const update = (scope) => {
    const block = scope.querySelector(BLOCK);
    if (!block) return;
    const fields = scope.querySelectorAll('[class*="gpo-"] input, [class*="gpo-"] textarea, [class*="gpo-"] select');
    block.classList.toggle('is-personalized', [...fields].some(isFilled));
  };

  const onEdit = (event) => {
    const field = event.target;
    if (!(field instanceof Element) || !field.closest('[class*="gpo-"]')) return;
    update(field.closest('.shopify-section') || document);
  };

  document.addEventListener('input', onEdit);
  document.addEventListener('change', onEdit);

  let busy = false;

  const buyNow = (block, destination) => {
    const component = block.closest('product-form-component');
    const addButton = component?.querySelector('.add-to-cart-button');
    if (!component || !addButton || addButton.disabled || addButton.getAttribute('disabled') === 'true') return;

    busy = true;
    block.setAttribute('aria-busy', 'true');
    document.body.classList.add('eg-buy-now');

    const reset = () => {
      busy = false;
      block.removeAttribute('aria-busy');
      document.body.classList.remove('eg-buy-now');
      document.removeEventListener('cart:update', onAdded);
    };

    // If Globo blocks the add (e.g. invalid text) no cart event arrives: give the page back.
    const timer = setTimeout(reset, 10000);

    function onAdded(event) {
      const data = event.detail?.data || {};
      if (data.source !== 'product-form-component' || !component.contains(event.target)) return;
      clearTimeout(timer);
      if (data.didError) {
        reset();
        return;
      }
      window.location.href = destination;
    }

    document.addEventListener('cart:update', onAdded);
    addButton.click();
  };

  // Capture phase runs before Shopify's own button handlers (they sit deeper in the tree).
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const block = target.closest(`${BLOCK}.is-personalized`);
      if (!block) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy) return;

      // "More payment options" goes to the regular checkout (every method); the button itself to Shop Pay.
      const destination = target.closest('.shopify-payment-button__more-options') ? CHECKOUT_URL : SHOP_PAY_URL;
      buyNow(block, destination);
    },
    true
  );

  // Back/forward cache: a restored page must not stay stuck in the busy state.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    busy = false;
    document.body.classList.remove('eg-buy-now');
    document.querySelectorAll(`${BLOCK}[aria-busy]`).forEach((block) => block.removeAttribute('aria-busy'));
  });
})();

/*
  Colour swatch pills are sized by the swatch script as they are drawn (window.egMeasure in the
  custom-liquid block). Re-measure when the web font arrives or the screen size changes, with
  transitions switched off so the correction never animates.
*/
(() => {
  const remeasure = () => {
    if (typeof window.egMeasure !== 'function') return;
    document.querySelectorAll('.product-details .eg-swatch-row').forEach((row) => {
      row.classList.add('eg-no-anim');
      window.egMeasure(row);
      row.offsetWidth; // apply the new sizes before transitions come back
      requestAnimationFrame(() => row.classList.remove('eg-no-anim'));
    });
  };

  document.fonts?.ready.then(remeasure);
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(remeasure, 150);
  });
})();

/*
  Instant colour change. The theme's variant picker re-fetches and morphs the product section on
  every option change, which reset the gallery to the first photo and briefly removed the buy
  buttons. Every colour here shares the same photos and price, so a colour click only needs to
  point the form at the right variant. If price or availability differ, fall back to the theme.
*/
(() => {
  const variants = () => window.egVariants || [];
  const norm = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();

  document.addEventListener(
    'click',
    (event) => {
      const swatch = event.target instanceof Element ? event.target.closest('.product-details .eg-swatch') : null;
      if (!swatch) return;

      const section = swatch.closest('.shopify-section') || document;
      const idInputs = section.querySelectorAll('form[data-type="add-to-cart-form"] input[name="id"], form.shopify-product-form input[name="id"]');
      if (!idInputs.length) return;

      // The option this swatch row belongs to ("Color", "Elastic Color" …), read from its radios.
      const fieldset = swatch.closest('.eg-fieldset');
      const colorIndex = (window.egOptionNames || []).findIndex((name) =>
        fieldset?.querySelector(`input[type="radio"][name^="${CSS.escape(name)}-"]`)
      );
      const current = variants().find((variant) => String(variant.id) === idInputs[0].value);
      const color = swatch.querySelector('.eg-swatch-name')?.textContent || '';
      if (colorIndex === -1 || !current) return;

      const target = variants().find(
        (variant) =>
          norm(variant.options[colorIndex]) === norm(color) &&
          variant.options.every((value, i) => i === colorIndex || value === current.options[i])
      );
      if (!target || target.price !== current.price || target.available !== current.available) return;

      // Handled here: keep the theme from re-rendering the section.
      event.stopPropagation();
      event.preventDefault();
      if (target.id === current.id) return;

      idInputs.forEach((input) => (input.value = target.id));

      const optionName = window.egOptionNames[colorIndex];
      section.querySelectorAll(`variant-picker input[type="radio"][name^="${CSS.escape(optionName)}-"]`).forEach((radio) => {
        radio.checked = norm(radio.value) === norm(color);
      });

      const row = swatch.closest('.eg-swatch-row');
      row?.querySelectorAll('.eg-swatch').forEach((el) => el.classList.toggle('eg-active', el === swatch));
      if (row) {
        const names = [...row.querySelectorAll('.eg-swatch-name')].map((el) => el.textContent);
        row.dataset.sig = `${names.join('|')}#${norm(color)}`;
      }
      const label = swatch.closest('.eg-fieldset')?.querySelector('.eg-color-label');
      if (label) label.textContent = color;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', target.id);
      history.replaceState(history.state, '', url);
    },
    true
  );
})();

/*
  3D models: the file is authored lying flat so it sits naturally on a table in AR. In the
  on-page viewer, stand it up (data-model-orientation) and let it turn slowly
  (data-model-auto-rotate). WebXR AR uses the same element, so drop the orientation while an AR
  session runs; Scene Viewer / Quick Look open the original file and are unaffected.
  The model also loads as soon as its slide is shown, so the flat poster only flashes briefly.
*/
(() => {
  const setup = (viewer) => {
    if (viewer.dataset.egReady) return;
    const wrapper = viewer.closest('product-model');
    if (!wrapper) return;
    viewer.dataset.egReady = 'true';

    const orientation = wrapper.dataset.modelOrientation;
    if (orientation) viewer.setAttribute('orientation', orientation);
    // Keep the upright image while the model streams in, instead of the generated flat preview.
    if (wrapper.dataset.modelPoster) viewer.setAttribute('poster', wrapper.dataset.modelPoster);

    if (wrapper.hasAttribute('data-model-auto-rotate')) {
      viewer.setAttribute('auto-rotate', '');
      viewer.setAttribute('rotation-per-second', '18deg');
      viewer.setAttribute('auto-rotate-delay', '3000');
      viewer.setAttribute('interaction-prompt', 'none');
    }
    if (orientation) viewer.setAttribute('camera-orbit', '-20deg 80deg auto');

    viewer.addEventListener('ar-status', (event) => {
      const status = event.detail?.status;
      if (status === 'session-started' || status === 'object-placed') {
        viewer.removeAttribute('orientation');
      } else if (status === 'not-presenting' && orientation) {
        viewer.setAttribute('orientation', orientation);
      }
    });
  };

  // Viewers are created from a <template> when the model is opened.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches('model-viewer')) setup(node);
        node.querySelectorAll?.('model-viewer').forEach(setup);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  // Open a model as soon as its slide is on screen.
  const autoOpen = () => {
    document.querySelectorAll('media-gallery product-model[data-model-orientation]').forEach((wrapper) => {
      if (wrapper.dataset.egWatched) return;
      wrapper.dataset.egWatched = 'true';
      new IntersectionObserver((entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0.6)) return;
        observer.disconnect();
        wrapper.querySelector('.deferred-media__poster-button')?.click();
      }, { threshold: [0.6] }).observe(wrapper);
    });
  };
  document.addEventListener('DOMContentLoaded', autoOpen);
  if (document.readyState !== 'loading') autoOpen();
})();
